import axios from 'axios';

// ============================================================
// Role Preview write gate.
// While an admin is previewing another role (localStorage `role_preview`),
// every write request (POST/PUT/PATCH/DELETE) pauses for an explicit
// "Submit change" confirmation before committing to the live database.
// The confirm dialog lives in RolePreviewBanner, which registers its
// resolver here. Reads are never gated.
// ============================================================

let confirmFn = null;

export const registerPreviewConfirm = (fn) => {
  confirmFn = fn;
  return () => {
    if (confirmFn === fn) confirmFn = null;
  };
};

// Session plumbing that must never be blocked by the gate.
const EXEMPT = /\/auth\/(login|logout|me|signup|refresh)|\/admin\/impersonate|\/admin\/content-overrides/;

export const confirmPreviewWrite = async (path = '', method = 'POST') => {
  if (!localStorage.getItem('role_preview')) return;
  if (EXEMPT.test(path)) return;
  if (!confirmFn) return;
  const ok = await confirmFn({ path, method });
  if (!ok) {
    const err = new Error('Change cancelled (role preview)');
    err.previewCancelled = true;
    throw err;
  }
};

// Global axios coverage — some pages call axios directly instead of lib/api.
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
if (!window.__avePreviewAxiosPatched) {
  window.__avePreviewAxiosPatched = true;
  axios.interceptors.request.use(async (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (WRITE_METHODS.has(method)) {
      await confirmPreviewWrite(config.url || '', method.toUpperCase());
    }
    return config;
  });
}

// Global fetch coverage — lib/api.js has ~70 named helpers built on raw
// fetch(), plus assorted component-level fetch calls. Patching window.fetch
// gates every backend write in one place. Scoped to our own /api/ URLs so
// third-party scripts (chat widgets, Stripe JS) are never interrupted.
if (!window.__avePreviewFetchPatched) {
  window.__avePreviewFetchPatched = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    try {
      const method = (
        (init && init.method) ||
        (input && typeof input === 'object' && input.method) ||
        'GET'
      ).toUpperCase();
      if (WRITE_METHODS.has(method.toLowerCase())) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/api\//.test(url)) {
          await confirmPreviewWrite(url, method);
        }
      }
    } catch (err) {
      if (err && err.previewCancelled) throw err;
      // Any unexpected gate error must never break normal requests.
    }
    return origFetch(input, init);
  };
}
